import type { AccessControlProvider } from "@refinedev/core";
import type { AdminRole, AdminIdentity } from "@/providers/types";
import { authProvider } from "@/providers/authProvider";

type ResourceActionMatrix = Record<string, Record<string, AdminRole[]>>;

const ALLOWED_ROLES: ResourceActionMatrix = {
  users: {
    list: ["support", "superadmin"],
    show: ["support", "moderator", "superadmin"],
    ban: ["superadmin"],
    unban: ["superadmin"],
    grant: ["support", "superadmin"],
    force_end: ["superadmin"],
    view_content: ["moderator", "superadmin"],
  },
  entitlement: {
    show: ["support", "superadmin"],
    list: ["support", "superadmin"],
  },
  operators: {
    list: ["superadmin"],
    create: ["superadmin"],
    edit: ["superadmin"],
    delete: ["superadmin"],
  },
  audit: {
    list: ["superadmin"],
    show: ["superadmin"],
  },
  places: {
    list: ["moderator", "superadmin"],
    takedown: ["moderator", "superadmin"],
    restore: ["moderator", "superadmin"],
  },
};

export type CanAccessParams = {
  resource: string | undefined;
  action: string;
  role: AdminRole | undefined;
};

export type CanAccessResult =
  | { can: true }
  | { can: false; reason: string };

export function canAccess({ resource, action, role }: CanAccessParams): CanAccessResult {
  if (!role) {
    return { can: false, reason: "Не авторизован" };
  }

  if (!resource) {
    return { can: false, reason: "Ресурс не указан" };
  }

  const resourceMatrix = ALLOWED_ROLES[resource];
  if (!resourceMatrix) {
    return { can: false, reason: "Раздел недоступен для вашей роли" };
  }

  const allowedRoles = resourceMatrix[action];
  if (!allowedRoles) {
    return { can: false, reason: "Действие недоступно для вашей роли" };
  }

  if (allowedRoles.includes(role)) {
    return { can: true };
  }

  return {
    can: false,
    reason: "Раздел недоступен для вашей роли",
  };
}

export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action }) => {
    const identity = (await authProvider.getIdentity?.()) as AdminIdentity | null;
    const role = identity?.role;
    return canAccess({ resource, action, role });
  },

  options: {
    buttons: {
      enableAccessControl: true,
      hideIfUnauthorized: true,
    },
  },
};
